'use client';

import { useEffect, useRef } from 'react';

interface ConversionEvent {
  eventName: string;
  eventCategory: string;
  eventLabel: string;
  eventValue?: string | number;
}

interface ConversionTrackingOverlayProps {
  trackScrollDepth?: boolean;
  trackSectionEngagement?: boolean;
  trackCallClicks?: boolean;
  scrollDepthThresholds?: number[];
}

const ConversionTrackingOverlay = ({
  trackScrollDepth = true,
  trackSectionEngagement = true,
  trackCallClicks = true,
  scrollDepthThresholds = [25, 50, 75, 90, 100],
}: ConversionTrackingOverlayProps) => {
  const trackedDepths = useRef<Set<number>>(new Set());
  const trackedSections = useRef<Set<string>>(new Set());
  const sessionStartTime = useRef<number>(Date.now());

  const trackEvent = (event: ConversionEvent) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event.eventName, {
        event_category: event.eventCategory,
        event_label: event.eventLabel,
        value: event.eventValue,
        timestamp: new Date().toISOString(),
      });
    }

    console.log('Conversion Event:', event);
  };

  useEffect(() => {
    trackEvent({
      eventName: 'page_view',
      eventCategory: 'engagement',
      eventLabel: 'landing_page_load',
      eventValue: window.location.pathname,
    });

    const handleScroll = () => {
      if (trackScrollDepth) {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrolled = window.scrollY;
        const scrollPercentage = (scrolled / scrollHeight) * 100;

        scrollDepthThresholds.forEach((threshold) => {
          if (
            scrollPercentage >= threshold &&
            !trackedDepths.current.has(threshold)
          ) {
            trackedDepths.current.add(threshold);
            trackEvent({
              eventName: 'scroll_depth',
              eventCategory: 'engagement',
              eventLabel: `scroll_${threshold}_percent`,
              eventValue: threshold,
            });
          }
        });
      }

      if (trackSectionEngagement) {
        const sections = [
          'hero',
          'value',
          'pricing',
          'channels',
          'benefits',
          'testimonials',
          'faq',
          'contact',
        ];

        sections.forEach((sectionId) => {
          const element = document.getElementById(sectionId);
          if (element && !trackedSections.current.has(sectionId)) {
            const rect = element.getBoundingClientRect();
            const isVisible =
              rect.top < window.innerHeight * 0.75 && rect.bottom > 0;

            if (isVisible) {
              trackedSections.current.add(sectionId);
              trackEvent({
                eventName: 'section_view',
                eventCategory: 'engagement',
                eventLabel: `section_${sectionId}_viewed`,
                eventValue: sectionId,
              });
            }
          }
        });
      }
    };

    const handleCallClick = (event: MouseEvent) => {
      if (trackCallClicks) {
        const target = event.target as HTMLElement;
        const callLink = target.closest('a[href^="tel:"]');

        if (callLink) {
          const phoneNumber = callLink.getAttribute('href')?.replace('tel:', '');
          trackEvent({
            eventName: 'click_to_call',
            eventCategory: 'conversion',
            eventLabel: 'phone_call_initiated',
            eventValue: phoneNumber,
          });
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const timeSpent = Math.floor((Date.now() - sessionStartTime.current) / 1000);
        trackEvent({
          eventName: 'session_duration',
          eventCategory: 'engagement',
          eventLabel: 'page_exit',
          eventValue: timeSpent,
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleCallClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleCallClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    trackScrollDepth,
    trackSectionEngagement,
    trackCallClicks,
    scrollDepthThresholds,
  ]);

  return null;
};

export default ConversionTrackingOverlay;